import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { listExceptions } from '../../api/originatorApi';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button } from '../../components/ui/Button';
import { CureExceptionModal } from '../../components/exceptions/CureExceptionModal';
import { FEATURES } from '../../config/features';

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DetailRow({ label, children, mono = false }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-eorig-border py-2.5 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
        {label}
      </span>
      <span
        className={`text-sm text-eorig-ink ${mono ? 'font-mono text-[12px]' : ''}`}
      >
        {children}
      </span>
    </div>
  );
}

export function ExceptionDetailPage() {
  const { exceptionId } = useParams();
  const location = useLocation();
  // Primary source: the row object passed via navigation state from the list.
  const stateException = location.state?.exception || null;

  const [exception, setException] = useState(stateException);
  const [loading, setLoading] = useState(!stateException);
  const [error, setError] = useState('');
  const [cureOpen, setCureOpen] = useState(false);

  // Fallback for direct URL / refresh (no nav state). There is no single
  // GET /v1/exceptions/{id} endpoint yet — see build-ledger wiring TODO
  // "Exception detail". Until it lands, best-effort locate within the list.
  useEffect(() => {
    if (stateException) {
      setException(stateException);
      return undefined;
    }
    let cancelled = false;

    async function locate() {
      setLoading(true);
      setError('');
      try {
        const response = await listExceptions({ page: 1, pageSize: 100 });
        if (cancelled) return;
        const found = (response.items || []).find(
          (item) => item.id === exceptionId
        );
        setException(found || null);
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.message || 'Unable to load exception.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    locate();
    return () => {
      cancelled = true;
    };
  }, [exceptionId, stateException]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-eorig-border bg-white p-6 text-sm text-eorig-muted shadow-eorig-card">
        Loading exception…
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
        <Link
          to="/exceptions"
          className="text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
        >
          ← Back to exceptions
        </Link>
      </div>
    );
  }

  if (!exception) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-eorig-border bg-white p-6 text-sm text-eorig-muted shadow-eorig-card">
          Exception{' '}
          <span className="font-mono text-eorig-ink">{exceptionId}</span> could
          not be found on the current queue. Open it from the exceptions list to
          view full detail.
        </div>
        <Link
          to="/exceptions"
          className="text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
        >
          ← Back to exceptions
        </Link>
      </div>
    );
  }

  const isCured = exception.status === 'CURED';

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-mono text-lg font-semibold text-eorig-ink">
              {exception.code}
            </h1>
            <StatusPill value={exception.severity} />
            <StatusPill value={exception.status} />
          </div>
          <p className="mt-1.5 text-[13px] text-eorig-muted">
            {exception.description || 'No description provided.'}
          </p>
          <Link
            to={`/packages/${exception.package_id}`}
            className="mt-1 inline-flex font-mono text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
          >
            Package {exception.package_id} →
          </Link>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="gradient"
            disabled={isCured}
            onClick={() => setCureOpen(true)}
          >
            {isCured ? 'Cured' : 'Cure exception'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
          <h2 className="text-sm font-semibold text-eorig-ink">Details</h2>
          <div className="mt-2">
            <DetailRow label="Exception ID" mono>
              {exception.id}
            </DetailRow>
            <DetailRow label="Code">{exception.code}</DetailRow>
            <DetailRow label="Severity">
              <StatusPill value={exception.severity} />
            </DetailRow>
            <DetailRow label="Status">
              <StatusPill value={exception.status} />
            </DetailRow>
            <DetailRow label="Owner">
              {exception.assigned_to || 'Unassigned'}
            </DetailRow>
            <DetailRow label="Package" mono>
              {exception.package_id}
            </DetailRow>
            {exception.document_id ? (
              <DetailRow label="Affected document" mono>
                {exception.document_id}
              </DetailRow>
            ) : null}
            {exception.fact_code ? (
              <DetailRow label="Linked fact">{exception.fact_code}</DetailRow>
            ) : null}
            {exception.evaluation_run_id ? (
              <DetailRow label="Admission evaluation" mono>
                {exception.evaluation_run_id}
              </DetailRow>
            ) : null}
            <DetailRow label="Required action">
              {String(exception.code || '').startsWith('ADM-')
                ? exception.fact_code
                  ? 'Confirm admission details'
                  : 'Cure admission exception'
                : 'Cure exception'}
            </DetailRow>
            <DetailRow label="Updated">
              {formatDateTime(exception.updated_at)}
            </DetailRow>
          </div>
        </section>

        <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
          <h2 className="text-sm font-semibold text-eorig-ink">Source preview</h2>
          {/*
            TODO(backend): exception source/evidence preview.
            No endpoint exposes the source document for an exception today.
            When a GET /v1/exceptions/{id}/source (or evidence) lands, render it
            here. Until then show a neutral empty state — never fabricate.
          */}
          <div className="mt-3 flex aspect-[4/3] items-center justify-center rounded-xl border border-dashed border-eorig-border bg-eorig-surface text-sm text-eorig-muted">
            Source preview available once evidence API is connected
          </div>

          {/*
            TODO(backend): IDA / AI hints (suggested doc type + confidence,
            expected program). Gated by FEATURES.aiReview until the AI review
            endpoint exists. The old hardcoded hints were removed to avoid
            fabricated data.
          */}
          {FEATURES.aiReview ? (
            <div className="mt-4">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                IDA hints
              </h3>
              <ul className="mt-2 space-y-1.5 text-sm text-eorig-ink/85">
                {(exception.ai_hints || []).map((hint, i) => (
                  <li key={i}>{hint}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      </div>

      <Link
        to="/exceptions"
        className="inline-flex text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
      >
        ← Back to exceptions
      </Link>

      <CureExceptionModal
        exception={cureOpen ? exception : null}
        onClose={() => setCureOpen(false)}
        onCured={(result) => {
          setCureOpen(false);
          // Update in place from the cure response (no single-exception GET).
          setException((prev) => ({ ...prev, ...(result || { status: 'CURED' }) }));
        }}
      />
    </div>
  );
}
